import { useEffect, useRef, useState } from 'react';

/** Per-country breakdown plus a total. */
export interface GeoStat {
  total: number;
  geo: Record<string, number>;
}

/** Snapshot pushed by the PresenceCounter Durable Object. */
export interface PresenceSnapshot {
  live: GeoStat;
  overall: GeoStat;
}

/**
 * Connects to the presence WebSocket exposed by the proxy Worker and keeps the
 * latest snapshot (live connections + all-time totals) in state.
 *
 * Progressive enhancement: if VITE_PROXY_URL is not configured, or the Worker /
 * Durable Object is not deployed yet, `connected` stays false and `snapshot`
 * stays null — the footer simply renders without the counter, never broken.
 */
export function usePresence(): { snapshot: PresenceSnapshot | null; connected: boolean } {
  const [snapshot, setSnapshot] = useState<PresenceSnapshot | null>(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const retryRef = useRef(0);
  const pingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedByUs = useRef(false);

  useEffect(() => {
    const proxy = import.meta.env.VITE_PROXY_URL;
    if (!proxy) return; // no backend configured → no counter

    // Derive the ws(s):// presence URL from the http(s):// proxy base.
    let presenceUrl: string;
    try {
      const u = new URL('/presence', proxy);
      u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
      presenceUrl = u.toString();
    } catch {
      return; // malformed proxy URL → bail quietly
    }

    const clearTimers = () => {
      if (pingRef.current) { clearInterval(pingRef.current); pingRef.current = null; }
      if (reconnectRef.current) { clearTimeout(reconnectRef.current); reconnectRef.current = null; }
    };

    const connect = () => {
      let ws: WebSocket;
      try {
        ws = new WebSocket(presenceUrl);
      } catch {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        retryRef.current = 0;
        // App-level keep-alive; the DO auto-responds "pong" without waking.
        pingRef.current = setInterval(() => {
          try { ws.readyState === WebSocket.OPEN && ws.send('ping'); } catch { /* noop */ }
        }, 25_000);
      };

      ws.onmessage = (ev) => {
        if (typeof ev.data !== 'string' || ev.data === 'pong') return;
        try {
          const data = JSON.parse(ev.data) as PresenceSnapshot;
          if (data && data.live && data.overall) setSnapshot(data);
        } catch { /* ignore malformed frames */ }
      };

      ws.onclose = () => {
        setConnected(false);
        clearTimers();
        if (!closedByUs.current) scheduleReconnect();
      };

      ws.onerror = () => {
        try { ws.close(); } catch { /* noop */ }
      };
    };

    const scheduleReconnect = () => {
      // Exponential backoff, capped at 30s.
      const delay = Math.min(30_000, 1_000 * 2 ** retryRef.current);
      retryRef.current += 1;
      reconnectRef.current = setTimeout(connect, delay);
    };

    closedByUs.current = false;
    connect();

    return () => {
      closedByUs.current = true;
      clearTimers();
      try { wsRef.current?.close(); } catch { /* noop */ }
    };
  }, []);

  return { snapshot, connected };
}
